import { useState } from "react";
import { ApiError, fetchFileBlobUrl } from "@/api/client";
import { useManagerPersonDocuments, useManagerTeam, useMyDocuments } from "@/api/queries";
import type { DocType, TeamMember } from "@/api/types";
import { PageHeader } from "@/app/page-header";
import { Avatar } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
} from "@/components/ui/dialog";
import { Icon } from "@/components/ui/icon";
import { Panel, PanelHeader } from "@/components/ui/panel";
import { EmptyState, ErrorState, SkeletonTable, SkeletonText } from "@/components/ui/states";
import { TBody, TD, TDPrimary, TH, THead, TR, TableWrap } from "@/components/ui/table";
import { toast } from "@/components/ui/toast";
import { formatTimestampDate } from "@/lib/format";
import { DOC_TYPE_LABEL } from "@/lib/labels";

/** The four types a manager may see. Bank details and addresses are never exposed here. */
const MANAGER_VISIBLE: DocType[] = ["aadhaar", "pan", "offer_letter", "compensation_sheet"];

export function ManagerDocumentsPage() {
  const team = useManagerTeam();
  const mine = useMyDocuments();
  const [openMember, setOpenMember] = useState<TeamMember | null>(null);

  const members = team.data?.members ?? [];

  if (team.error) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeader eyebrow="Scoped to your direct reports" title="Team documents" />
        <Panel>
          <ErrorState message={team.error.message} onRetry={() => void team.refetch()} />
        </Panel>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        eyebrow="Scoped to your direct reports"
        title="Team documents"
        sub="Your own record, plus the identity and offer documents for each direct report. Bank details, addresses, blood group and emergency contacts stay private to HR and the person themselves."
      />

      <Panel>
        <PanelHeader
          title="Your own record"
          note="Everything you have on file, including details a manager never sees for their reports"
        />
        {mine.isLoading ? (
          <SkeletonText lines={4} className="p-4" />
        ) : (
          <ul className="divide-y divide-line">
            {(mine.data?.documents ?? []).length === 0 ? (
              <li>
                <EmptyState
                  icon="document"
                  title="No documents on file"
                  body="Your own uploads appear here. Add them from Account settings if anything is missing."
                />
              </li>
            ) : (
              (mine.data?.documents ?? []).map((doc) => (
                <li key={doc.doc_type} className="flex items-center gap-3 px-4 py-2.5">
                  <Icon name="check-circle" size={20} className="shrink-0 text-good" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-ink">
                      {DOC_TYPE_LABEL[doc.doc_type]}
                    </p>
                    <p className="truncate text-xs text-muted">{doc.original_name}</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconLeft="view"
                    onClick={() => void openOwnDocument(doc.doc_type)}
                  >
                    View
                  </Button>
                </li>
              ))
            )}
          </ul>
        )}
      </Panel>

      <Panel>
        <PanelHeader
          title="Direct reports"
          note={`${members.length} ${members.length === 1 ? "person" : "people"} · view only`}
        />
        {team.isLoading ? (
          <SkeletonTable rows={5} cols={3} />
        ) : members.length === 0 ? (
          <EmptyState
            icon="team"
            title="No direct reports yet"
            body="Employees appear here once HR maps their account to your team."
          />
        ) : (
          <TableWrap>
            <THead>
              <TH>Person</TH>
              <TH>Designation</TH>
              <TH>
                <span className="sr-only">Open</span>
              </TH>
            </THead>
            <TBody>
              {members.map((member) => (
                <TR key={member.id}>
                  <TDPrimary>
                    <span className="flex items-center gap-2">
                      <Avatar name={member.name} size="sm" />
                      <span className="min-w-0">
                        <span className="block truncate">{member.name ?? "Unnamed"}</span>
                        <span className="block truncate font-mono text-xs font-normal text-muted">
                          {member.email}
                        </span>
                      </span>
                    </span>
                  </TDPrimary>
                  <TD>{member.designation}</TD>
                  <TD>
                    <Button variant="ghost" size="sm" onClick={() => setOpenMember(member)}>
                      View documents
                    </Button>
                  </TD>
                </TR>
              ))}
            </TBody>
          </TableWrap>
        )}
      </Panel>

      {openMember && <MemberDocuments member={openMember} onClose={() => setOpenMember(null)} />}
    </div>
  );
}

async function openOwnDocument(docType: DocType) {
  try {
    const url = await fetchFileBlobUrl(`/api/documents/me/file/${docType}`);
    window.open(url, "_blank", "noopener,noreferrer");
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (err) {
    toast.error(err instanceof ApiError ? err.message : "Could not open that document.");
  }
}

function MemberDocuments({ member, onClose }: { member: TeamMember; onClose: () => void }) {
  const record = useManagerPersonDocuments(member.id);
  const documents = record.data?.documents ?? [];

  async function open(docType: DocType) {
    try {
      const url = await fetchFileBlobUrl(`/api/manager/documents/${member.id}/file/${docType}`);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Could not open that document.");
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader title={member.name ?? member.email} description={member.designation} />
        <DialogBody className="flex flex-col gap-3">
          {record.isLoading ? (
            <SkeletonText lines={4} />
          ) : record.error ? (
            <ErrorState message={record.error.message} onRetry={() => void record.refetch()} />
          ) : (
            <>
              <ul className="flex flex-col gap-2">
                {MANAGER_VISIBLE.map((docType) => {
                  const doc = documents.find((d) => d.doc_type === docType);
                  return (
                    <li
                      key={docType}
                      className="flex items-center justify-between gap-3 rounded-lg border border-line p-3"
                    >
                      <div className="flex min-w-0 items-center gap-2.5">
                        <Icon
                          name={doc ? "check-circle" : "document"}
                          size={20}
                          className={doc ? "text-good" : "text-faint"}
                        />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-ink">{DOC_TYPE_LABEL[docType]}</p>
                          <p className="truncate text-xs text-muted">
                            {doc
                              ? `Uploaded ${formatTimestampDate(doc.uploaded_at)}`
                              : "Not on file"}
                          </p>
                        </div>
                      </div>
                      {doc && (
                        <Button variant="ghost" size="sm" iconLeft="view" onClick={() => void open(docType)}>
                          View
                        </Button>
                      )}
                    </li>
                  );
                })}
              </ul>
              <p className="flex items-start gap-2 text-xs leading-relaxed text-muted">
                <Icon name="lock" size={16} className="mt-px shrink-0 text-faint" />
                <span>
                  View only. Bank details, addresses, blood group and emergency contacts are not
                  shared with managers, and documents cannot be downloaded from here.
                </span>
              </p>
            </>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="primary" onClick={onClose}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
